import React, { useState, useEffect, useContext } from "react";
import {
  parseParameters,
  parseBypassParameters,
  composeApplyCommand,
} from "./api";
import ActionsContext from "common/ActionsContext";
import SettingsView from "common/SettingsView";
import ArrangeInColumns from "common/layouts/ArrangeInColumns";
import BypassSettings from "./BypassSettings";
import TCPAndShapingSettings from "./TCPAndShapingSettings";
import { GreSettings } from "./GreSettings";
import { parseClusterResponse } from "common/api";
import BqnContext from "common/BqnContext";
import Request from "common/Request";
import Header from "common/Header";
import { MplsSettings } from "./MplsSettings";
import FoldablePanel from "common/FoldablePanel";
import { BqnTabs } from "./BqnTabs";
import { mergeAndMetadata } from "./utils";

const doesNothing = () => {};

const CANT_PROCEED_ERROR =
  "Cannot proceed while there are pending config changes." +
  " Try again later.";

const clear = () =>
  ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

const arrangeIntoSettings = ([settings = {}, bypass = {}]) => ({
  ...settings,
  bypass,
});

const OptimizationContextMenu = ({ doLoad = doesNothing }) => {
  const [optionsEnabled, setOptionsEnabled] = useState(true);
  const actions = useContext(ActionsContext);
  useEffect(
    () =>
      actions.recv("state-changed", (state) =>
        setOptionsEnabled(state.enabled === true),
      ),
    [],
  );

  return (
    <ul className="context-menu header-dropdown m-r--5">
      <li>
        <a
          onClick={doLoad}
          data-toggle="cardloading"
          data-loading-effect="pulse"
          title="Refresh"
        >
          <i className="material-icons">refresh</i>
        </a>
      </li>
      {optionsEnabled === true ? (
        <li className="dropdown hidden-to-operators">
          <a
            className="dropdown-toggle"
            data-toggle="dropdown"
            role="button"
            aria-haspopup="true"
            aria-expanded="false"
            title="Options"
          >
            <i className="material-icons">more_vert</i>
          </a>
          <ul className="dropdown-menu pull-right">
            <li>
              <a onClick={() => actions.send("new-client")}>Add Client...</a>
            </li>
          </ul>
        </li>
      ) : null}
    </ul>
  );
};

const OptimizationConfig = () => {
  const [request, setRequest] = useState(null);
  const [bqnDisplayed, setBqnDisplayed] = useState(null);

  const {
    bqnId,
    onlineBqns,
    clusterStatus: { mode },
  } = useContext(BqnContext);

  useEffect(() => {
    load();
  }, []);

  useEffect(() => {
    setBqnDisplayed(bqnId);
  }, [bqnId]);

  const load = () => {
    const req = getOptimizationSettings();
    setRequest(req);
    return req;
  };

  const checkNothingPending = () =>
    ifCl.run("show config diff").then((response) => {
      if (response.length > 0) {
        throw new Error(CANT_PROCEED_ERROR);
      }
    });

  const clearConfigAttempt = () =>
    ifCl.run("configure\nclear config changes\n", /*is batch*/ true);

  const notifyAndRevert = (error) => {
    error = error.message === undefined ? new Error(error) : error;
    clearConfigAttempt();
    throw error;
  };

  const submitCommand = (command) =>
    ifCl.run(command + "\n", /*is batch*/ true);

  const apply = (settings, originalSettings = {}, bqn, metadata = null) => {
    return checkNothingPending()
      .then(() =>
        composeApplyCommand(
          originalSettings,
          settings,
          mode === "cluster" ? bqn : undefined,
          metadata,
          onlineBqns
        ),
      )
      .then(submitCommand)
      .then(() => load())
      .catch(notifyAndRevert);
  };

  const getParameters = () =>
    ifCl
      .run("show pkteng parameters")
      .then((res) => parseClusterResponse(res, parseParameters));

  const getOptimizationSettings = () =>
    Promise.all([getParameters(), getBypassSettings()])
      .then(arrangeIntoSettings)
      .catch(console.error);

  const getBypassSettings = () =>
    ifCl.run("show pkteng bypass").then(parseBypassParameters);

  const getData = (initial, data) => {
    if (initial) {
      return Promise.resolve(data);
    } else {
      return load();
    }
  };
  return (
    <>
      <div className="container-lg">
        <div className="row clearfix">
          <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
            <div className="card">
              <Header
                title="Optimization settings"
                subtitle={
                  <small>
                    Press <code>Apply</code> button at the end of the page to
                    set the new configuration values.
                  </small>
                }
                contextMenu={<OptimizationContextMenu doLoad={() => load()} />}
                bqnSelector={true}
              />
            </div>
          </div>
        </div>
      </div>
      <Request during={request}>
        {(result) => {
          if (bqnDisplayed === null) {
            return null;
          }

          let data;
          let meta;
          if (bqnDisplayed === "all") {
            const { merge, metadata } = mergeAndMetadata(result);
            data = merge;
            meta = metadata;
          } else {
            data = {
              ...result[bqnDisplayed],
              bypass: result.bypass[bqnDisplayed],
            };
            meta = null;
          }

          return (
            <>
              <BqnTabs value={bqnDisplayed} />
              <SettingsView
                key={`settings-bqn-${bqnDisplayed}`}
                className="container-lg"
                load={(initial) => getData(initial, data)}
                apply={(settings, originalSettings) =>
                  apply(settings, originalSettings, bqnDisplayed, meta)
                }
                clear={clear}
                ContextMenu={OptimizationContextMenu}
                applyActionClassName="hidden-to-operators"
                wrapIntoForm={false}
                noHeader={true}
              >
                <ShowOptimizationSettings bqn={bqnDisplayed} metadata={meta} />
              </SettingsView>
            </>
          );
        }}
      </Request>
    </>
  );
};

let mplsGrenOpen = false;

const ShowOptimizationSettings = ({
  defaults,
  state,
  onStateUpdate = doesNothing,
  bqn,
  metadata,
}) => {
  const updateSettings = (update) => {
    onStateUpdate(update);
  };

  const updateBypassSettings = (update) => {
    onStateUpdate({
      bypass: {
        ...state.bypass,
        ...update,
      },
    });
  };

  return (
    <div className="margin-t-20">
      <div className="body margin-t-20">
        <div className="row">
          <ArrangeInColumns alignToTop={true}>
            <div>
              <BypassSettings
                onChange={updateBypassSettings}
                defaults={defaults.bypass}
                state={state.bypass}
                bqn={bqn}
                metadata={metadata?.bypass}
              />
            </div>
            <div>
              <TCPAndShapingSettings
                onChange={updateSettings}
                defaults={defaults}
                state={state}
                bqn={bqn}
                metadata={metadata}
              />
              <FoldablePanel
                title="MPLS and GRE Options"
                opened={mplsGrenOpen}
                onChange={(value) => (mplsGrenOpen = value)}
                className="margin-t-15"
              >
                <GreSettings
                  onChange={updateSettings}
                  defaults={defaults}
                  state={state}
                  bqn={bqn}
                  metadata={metadata}
                />
                <MplsSettings
                  onChange={updateSettings}
                  defaults={defaults}
                  state={state}
                  bqn={bqn}
                  metadata={metadata}
                />
              </FoldablePanel>
            </div>
          </ArrangeInColumns>
        </div>
      </div>
    </div>
  );
};

export default OptimizationConfig;
